/**
 * THEME CONTEXT
 * Automatic dark/light theme switching based on system preferences and time
 */

import { createContext, useContext, useEffect, useState, ReactNode } from 'react'

type Theme = 'light' | 'dark' | 'auto'

interface ThemeContextType {
  theme: Theme
  setTheme: (theme: Theme) => void
  actualTheme: 'light' | 'dark' // The actual applied theme (resolved from 'auto')
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(() => {
    // Try to get saved preference
    const savedTheme = localStorage.getItem('theme') as Theme
    return savedTheme || 'auto'
  })

  const [actualTheme, setActualTheme] = useState<'light' | 'dark'>('light')

  useEffect(() => {
    const root = document.documentElement

    const applyTheme = (isDark: boolean) => {
      if (isDark) {
        root.classList.add('dark')
        setActualTheme('dark')
      } else {
        root.classList.remove('dark')
        setActualTheme('light')
      }
    }

    if (theme === 'auto') {
      // Check system preference
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

      // Also check time (auto dark mode between 6 PM and 6 AM)
      const hour = new Date().getHours()
      const isNightTime = hour >= 18 || hour < 6

      // Apply dark mode if system prefers it OR if it's nighttime
      const shouldBeDark = mediaQuery.matches || isNightTime
      applyTheme(shouldBeDark)

      // Listen for system preference changes
      const handleChange = (e: MediaQueryListEvent) => {
        const hour = new Date().getHours()
        const isNightTime = hour >= 18 || hour < 6
        applyTheme(e.matches || isNightTime)
      }

      mediaQuery.addEventListener('change', handleChange)

      // Update every hour to check for time-based changes
      const interval = setInterval(() => {
        const hour = new Date().getHours()
        const isNightTime = hour >= 18 || hour < 6
        applyTheme(mediaQuery.matches || isNightTime)
      }, 60 * 60 * 1000) // Check every hour

      return () => {
        mediaQuery.removeEventListener('change', handleChange)
        clearInterval(interval)
      }
    } else {
      // Manual theme selection
      applyTheme(theme === 'dark')
    }
  }, [theme])

  // Save theme preference
  useEffect(() => {
    localStorage.setItem('theme', theme)
  }, [theme])

  return (
    <ThemeContext.Provider value={{ theme, setTheme, actualTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}
